import {
  Business,
  getPrimaryCategory,
  getStaticCategory,
  getStaticFilter,
} from '@/api/models';
import { EntityLogsTab } from '@/components/EntityLogsTab';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { Icon } from '@/components/ui/Icon';
import { TabNavigation } from '@/components/ui/TabNavigation';
import { BusinessFormData } from '@/pages/businesses/hooks/useBusinessMutations';
import {
  mdiChartBox,
  mdiClock,
  mdiDelete,
  mdiMapMarker,
  mdiPencil,
  mdiPhone,
  mdiTag,
  mdiWeb,
} from '@mdi/js';
import * as React from 'react';
import { BusinessForm } from './BusinessForm';
import { BusinessMapProvidersTab } from './BusinessMapProvidersTab';
import { BusinessReviewsTab } from './BusinessReviewsTab';

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export interface BusinessDrawerViewProps {
  // State
  isOpen: boolean;
  business: Business | null;
  mode: 'view' | 'edit';

  // Loading
  isLoading: boolean;
  isFormLoading: boolean;
  isTogglingReview: boolean;
  isDeletingImage: boolean;
  isSettingPrimaryImage: boolean;

  // Actions
  onClose: () => void;
  onEdit: (business: Business) => void;
  onDelete: (business: Business) => void;
  onToggleReview: (
    businessId: number,
    reviewId: number,
    isEvaluated: boolean,
  ) => void;
  onFormSubmit: (data: BusinessFormData) => Promise<void>;
  onDeleteImage: (businessId: number, imageId: number) => void;
  onSetPrimaryImage: (businessId: number, imageId: number) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Active':
      return 'bg-green-100 text-green-700';
    case 'Pending':
      return 'bg-yellow-100 text-yellow-700';
    case 'Inactive':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-grey-100 text-grey-700';
  }
};

const getScoreColor = (score: number) => {
  if (score >= 70) return 'text-green-600';
  if (score >= 40) return 'text-yellow-600';
  return 'text-red-600';
};

const getScoreBgColor = (score: number) => {
  if (score >= 70) return 'bg-green-500';
  if (score >= 40) return 'bg-yellow-500';
  return 'bg-red-500';
};

const getConfidenceLabel = (confidence: number, reviewCount: number) => {
  if (reviewCount === 0) return 'No reviews yet';
  if (confidence >= 0.8) return 'High confidence';
  if (confidence >= 0.5) return 'Medium confidence';
  return 'Low confidence';
};

export const BusinessDrawerView: React.FC<BusinessDrawerViewProps> = ({
  // State
  isOpen,
  business,
  mode,

  // Loading
  isLoading,
  isFormLoading,
  isTogglingReview,
  isDeletingImage,
  isSettingPrimaryImage,

  // Actions
  onClose,
  onEdit,
  onDelete,
  onToggleReview,
  onFormSubmit,
  onDeleteImage,
  onSetPrimaryImage,
}) => {
  const [activeTab, setActiveTab] = React.useState<
    'details' | 'reviews' | 'refresh' | 'logs'
  >('details');

  const getTitle = () => {
    switch (mode) {
      case 'edit':
        return business ? `Edit ${business.name}` : 'Edit Business';
      case 'view':
      default:
        return business ? business.name : 'Business Details';
    }
  };

  if (isLoading) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} title='Loading...' width='lg'>
        <div className='p-6 flex items-center justify-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={getTitle()} width='lg'>
      {mode === 'view' && business && (
        <div className='flex flex-col h-full'>
          {/* Tabs */}
          <TabNavigation
            tabs={[
              { id: 'details', label: 'Details' },
              {
                id: 'reviews',
                label: 'Reviews',
                count: business.reviews?.length || 0,
              },
              { id: 'refresh', label: 'Map Providers' },
              { id: 'logs', label: 'Logs' },
            ]}
            activeTab={activeTab}
            onTabChange={tab =>
              setActiveTab(tab as 'details' | 'reviews' | 'refresh' | 'logs')
            }
          />

          {activeTab === 'details' && (
            <div className='p-6 space-y-6 overflow-y-auto flex-1'>
              {/* Business Header */}
              <div className='flex items-start space-x-4 pb-6 border-b border-grey-200'>
                <div className='w-20 h-20 rounded-lg overflow-hidden bg-grey-100 flex-shrink-0'>
                  {business.images?.find(img => img.primary)?.image
                    ?.primaryUrl ? (
                    <img
                      src={
                        business.images.find(img => img.primary)?.image
                          ?.primaryUrl
                      }
                      alt={business.name}
                      className='w-full h-full object-cover'
                    />
                  ) : (
                    <div className='w-full h-full flex items-center justify-center text-grey-400'>
                      No image
                    </div>
                  )}
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center space-x-3'>
                    <h3 className='text-xl font-semibold text-grey-900 break-words'>
                      {business.name}
                    </h3>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(business.status || '')}`}
                    >
                      {business.status}
                    </span>
                  </div>
                  <p className='text-grey-500 mt-1'>{business.address}</p>
                  <p className='text-grey-500'>
                    {business.city}, {business.province} {business.postalCode}
                  </p>
                  <p className='text-grey-500'>{business.country}</p>
                </div>
              </div>

              {/* Contact Information */}
              <div className='bg-grey-50 rounded-lg p-4'>
                <h4 className='text-sm font-medium text-grey-700 mb-3'>
                  Contact Information
                </h4>
                <dl className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  {business.phone && (
                    <div className='flex items-center space-x-2'>
                      <Icon
                        path={mdiPhone}
                        size={16}
                        className='text-grey-400'
                      />
                      <div>
                        <dt className='text-xs text-grey-500'>Phone</dt>
                        <dd className='text-sm text-grey-900'>
                          {business.phone}
                        </dd>
                      </div>
                    </div>
                  )}
                  {business.website && (
                    <div className='flex items-start space-x-2 min-w-0'>
                      <Icon
                        path={mdiWeb}
                        size={16}
                        className='text-grey-400 flex-shrink-0 mt-0.5'
                      />
                      <div className='min-w-0'>
                        <dt className='text-xs text-grey-500'>Website</dt>
                        <dd className='text-sm text-blue-600'>
                          <a
                            href={business.website}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='hover:underline break-all'
                          >
                            {business.website}
                          </a>
                        </dd>
                      </div>
                    </div>
                  )}
                </dl>
              </div>

              {/* Work-Friendly Score */}
              {(() => {
                const extBusiness = business as Business & {
                  workFriendlyScore?: number | null;
                  scoreConfidence?: number;
                  approvedReviewCount?: number;
                  scoreCalculatedAt?: string;
                };
                const score = extBusiness.workFriendlyScore;
                const hasScore = score !== null && score !== undefined;
                const confidence = extBusiness.scoreConfidence ?? 0;
                const reviewCount = extBusiness.approvedReviewCount ?? 0;
                const calculatedAt = extBusiness.scoreCalculatedAt;

                return (
                  <div className='bg-grey-50 rounded-lg p-4'>
                    <h4 className='text-sm font-medium text-grey-700 mb-3'>
                      <Icon
                        path={mdiChartBox}
                        size={16}
                        className='inline mr-1'
                      />
                      Work-Friendly Score
                    </h4>
                    {hasScore ? (
                      <div className='flex items-center gap-6'>
                        {/* Score Display */}
                        <div className='flex flex-col items-center'>
                          <div
                            className={`text-4xl font-bold ${getScoreColor(score)}`}
                          >
                            {Math.round(score)}
                          </div>
                          <div className='text-xs text-grey-500'>
                            out of 100
                          </div>
                        </div>

                        {/* Score Bar */}
                        <div className='flex-1'>
                          <div className='h-3 bg-grey-200 rounded-full overflow-hidden'>
                            <div
                              className={`h-full ${getScoreBgColor(score)} transition-all duration-300`}
                              style={{ width: `${score}%` }}
                            />
                          </div>
                          <div className='mt-2 flex justify-between text-xs text-grey-500'>
                            <span>
                              {getConfidenceLabel(confidence, reviewCount)}
                            </span>
                            <span>
                              {reviewCount} review{reviewCount !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className='text-grey-400 text-sm'>
                        No reviews yet
                      </div>
                    )}

                    {/* Last Calculated */}
                    {calculatedAt && (
                      <div className='mt-3 pt-3 border-t border-grey-200 text-xs text-grey-500'>
                        Last calculated:{' '}
                        {new Date(calculatedAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Categories & Filters */}
              <div className='bg-grey-50 rounded-lg p-4'>
                <h4 className='text-sm font-medium text-grey-700 mb-3'>
                  <Icon path={mdiTag} size={16} className='inline mr-1' />
                  Categories & Filters
                </h4>
                <div className='space-y-3'>
                  <div>
                    <dt className='text-xs text-grey-500 mb-1'>Categories</dt>
                    <dd className='flex flex-wrap gap-1'>
                      {(() => {
                        const primaryCategory = getPrimaryCategory(
                          business.businessCategories,
                        );
                        const otherCategories =
                          business.businessCategories?.filter(
                            bc => bc !== primaryCategory,
                          ) || [];

                        if (!primaryCategory) {
                          return (
                            <span className='text-grey-400 text-sm'>
                              No categories
                            </span>
                          );
                        }

                        const primaryStatic = getStaticCategory(
                          primaryCategory.category?.name || '',
                        );

                        return (
                          <>
                            <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700'>
                              {primaryStatic?.emoji}{' '}
                              {primaryStatic?.label ||
                                primaryCategory.category?.name}
                            </span>
                            {otherCategories.map((bc, index) => {
                              const staticCategory = getStaticCategory(
                                bc.category?.name || '',
                              );
                              return (
                                <span
                                  key={index}
                                  className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-grey-100 text-grey-600'
                                >
                                  {staticCategory?.emoji}{' '}
                                  {staticCategory?.label || bc.category?.name}
                                </span>
                              );
                            })}
                          </>
                        );
                      })()}
                    </dd>
                  </div>
                  <div>
                    <dt className='text-xs text-grey-500 mb-1'>Filters</dt>
                    <dd className='flex flex-wrap gap-1'>
                      {business.filters?.map((bf, index) => {
                        const staticFilter = getStaticFilter(
                          bf.filter?.name || '',
                        );
                        return (
                          <span
                            key={index}
                            className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700'
                          >
                            {staticFilter?.emoji}{' '}
                            {staticFilter?.label || bf.filter?.name}
                          </span>
                        );
                      })}
                      {(!business.filters || business.filters.length === 0) && (
                        <span className='text-grey-400 text-sm'>
                          No filters
                        </span>
                      )}
                    </dd>
                  </div>
                </div>
              </div>

              {/* Opening Hours */}
              <div className='bg-grey-50 rounded-lg p-4'>
                <h4 className='text-sm font-medium text-grey-700 mb-3'>
                  <Icon path={mdiClock} size={16} className='inline mr-1' />
                  Opening Hours
                </h4>
                {business.openingHours && business.openingHours.length > 0 ? (
                  <div className='grid grid-cols-1 gap-2'>
                    {DAY_NAMES.map((day, dayIndex) => {
                      const hours = business.openingHours?.filter(
                        h => Number(h.dayOfWeek) === dayIndex,
                      );
                      return (
                        <div
                          key={dayIndex}
                          className='flex justify-between text-sm'
                        >
                          <span className='font-medium text-grey-700'>
                            {day}
                          </span>
                          <span className='text-grey-600'>
                            {hours && hours.length > 0
                              ? hours
                                  .map(
                                    h =>
                                      `${h.openTime?.substring(0, 5)} - ${h.closeTime?.substring(0, 5)}`,
                                  )
                                  .join(', ')
                              : 'Closed'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className='text-grey-400 text-sm'>No hours set</p>
                )}
              </div>

              {/* Images Gallery */}
              {business.images && business.images.length > 0 && (
                <div className='bg-grey-50 rounded-lg p-4'>
                  <h4 className='text-sm font-medium text-grey-700 mb-3'>
                    Images ({business.images.length})
                  </h4>
                  <div className='grid grid-cols-3 gap-2'>
                    {business.images.map((img, index) => (
                      <div
                        key={index}
                        className={`aspect-square rounded-lg overflow-hidden relative ${
                          img.primary ? 'ring-2 ring-blue-500' : ''
                        }`}
                      >
                        <img
                          src={img.image?.primaryUrl}
                          alt={`${business.name} ${index + 1}`}
                          className='w-full h-full object-cover'
                        />
                        {img.primary && (
                          <span className='absolute top-1 left-1 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded'>
                            Primary
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Location */}
              <div className='bg-grey-50 rounded-lg p-4'>
                <h4 className='text-sm font-medium text-grey-700 mb-3'>
                  <Icon path={mdiMapMarker} size={16} className='inline mr-1' />
                  Location
                </h4>
                <dl className='grid grid-cols-2 gap-4'>
                  <div>
                    <dt className='text-xs text-grey-500'>Latitude</dt>
                    <dd className='text-sm font-mono text-grey-900'>
                      {business.location?.coordinates?.[1]?.toFixed(6) || 'N/A'}
                    </dd>
                  </div>
                  <div>
                    <dt className='text-xs text-grey-500'>Longitude</dt>
                    <dd className='text-sm font-mono text-grey-900'>
                      {business.location?.coordinates?.[0]?.toFixed(6) || 'N/A'}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Quick Actions */}
              <div className='bg-grey-50 rounded-lg p-4'>
                <h4 className='text-sm font-medium text-grey-700 mb-3'>
                  Quick Actions
                </h4>
                <div className='flex flex-wrap gap-2'>
                  <Button
                    icon={mdiPencil}
                    label='Edit Business'
                    color='secondary'
                    small
                    onClick={() => onEdit(business)}
                  />
                  <Button
                    icon={mdiDelete}
                    label='Delete Business'
                    color='danger'
                    small
                    outline
                    onClick={() => onDelete(business)}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className='flex-1 overflow-y-auto'>
              <BusinessReviewsTab
                business={business}
                onToggleReview={onToggleReview}
                isTogglingReview={isTogglingReview}
              />
            </div>
          )}

          {activeTab === 'refresh' && (
            <div className='flex-1 overflow-y-auto'>
              <BusinessMapProvidersTab business={business} />
            </div>
          )}

          {activeTab === 'logs' && (
            <div className='flex-1 overflow-y-auto'>
              <EntityLogsTab entityType='business' entityId={business.id} />
            </div>
          )}
        </div>
      )}

      {mode === 'edit' && (
        <BusinessForm
          business={business}
          onSubmit={onFormSubmit}
          onCancel={onClose}
          loading={isFormLoading}
          onDeleteImage={onDeleteImage}
          onSetPrimaryImage={onSetPrimaryImage}
          isDeletingImage={isDeletingImage}
          isSettingPrimaryImage={isSettingPrimaryImage}
        />
      )}
    </Drawer>
  );
};
